import { useEffect, useState, type ImgHTMLAttributes } from 'react';

import { useBlobAccessUrl } from '@/hooks/useBlobAccessUrl';
import { cn } from '@/utils/cn';

import { ImagePlaceholderBox } from './ImagePlaceholderBox.tsx';

type BlobImageFallback = {
	isGenerating?: boolean;
};

// `src` is omitted from the base props before being redeclared: intersecting with
// ImgHTMLAttributes' own `src?: string` collapses the union back to `string | undefined`,
// which silently dropped the `null` this component is explicitly built to handle (it
// renders the placeholder for it).
type BlobImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
	src: string | null | undefined;
	fallback?: BlobImageFallback;
};

export function BlobImage({
	src,
	fallback,
	onError,
	className,
	...props
}: BlobImageProps) {
	const accessUrl = useBlobAccessUrl(src);
	const [errored, setErrored] = useState(false);

	useEffect(() => {
		setErrored(false);
	}, [src]);

	if (!fallback) {
		if (!accessUrl) return null;
		return (
			<img {...props} className={className} src={accessUrl} onError={onError} />
		);
	}

	if (accessUrl && !errored) {
		return (
			<img
				{...props}
				className={className}
				src={accessUrl}
				onError={(e) => {
					setErrored(true);
					onError?.(e);
				}}
			/>
		);
	}

	const ariaLabel =
		typeof props['aria-label'] === 'string' ? props['aria-label'] : undefined;

	if (fallback.isGenerating) {
		return (
			<ImagePlaceholderBox
				errored={false}
				ariaLabel={ariaLabel}
				ariaBusy
				className={cn(className, 'relative overflow-hidden bg-gray-100')}
			/>
		);
	}

	return (
		<ImagePlaceholderBox
			errored
			role="img"
			ariaLabel={ariaLabel}
			className={cn(
				className,
				'flex items-center justify-center bg-gray-100 text-(--text-muted)',
			)}
		/>
	);
}
